import type { CSSProperties, ReactNode } from 'react';

import { Box } from '../primitives/Box.js';
import { HStack, VStack } from '../primitives/Stack.js';
import { Text } from '../primitives/Text.js';
import { cx } from '../primitives/cx.js';
import { LEAD_WIDTH } from './rowMetrics.js';

export type HabitCardVariant = 'card' | 'row';

export type HabitCardProps = {
  name: string;
  subtext?: string;
  /** Struck through and dimmed. */
  done?: boolean;
  /**
   * Collapse to a slim row. Completed habits fold IN PLACE — they never
   * resort to the bottom, because a list that rearranges itself under a
   * thumb is a list you stop trusting.
   */
  folded?: boolean;
  /**
   * `card` is the phone: a bordered row in a stack of rows.
   * `row` is the desktop wall chart: no border, a hairline underneath, and a
   * fixed-width lead block that sticks to the left while the days scroll past.
   * A `row` never folds — uniform height is what keeps the columns aligned.
   */
  variant?: HabitCardVariant;
  /** Right edge: the completion circle, preceded by the day strip on desktop. */
  trailing?: ReactNode;
  /** Under the name: the floor button. Card variant only. */
  below?: ReactNode;
  /** Left edge: the drag handle. */
  leading?: ReactNode;
  className?: string;
  /** Only ever a transform, set by the drag while it is in flight. Anything
   *  that isn't per-frame belongs in `className` with the rest of the design. */
  style?: CSSProperties;
};

/**
 * One row of the only list that matters.
 *
 * Deliberately dumb: it owns spacing and type, and nothing else. What sits at
 * either edge is the caller's choice, which is what lets one row serve the
 * phone list, the folded list, and the desktop wall chart where the same row
 * carries a month of squares.
 */
export function HabitCard({
  name,
  subtext,
  done = false,
  folded = false,
  variant = 'card',
  trailing,
  below,
  leading,
  className,
  style,
}: HabitCardProps) {
  const wide = variant === 'row';
  const collapsed = folded && !wide;

  const label = (
    <VStack gap={0} className="min-w-0 flex-1">
      <Text
        variant="label"
        tone={done ? 'subtle' : 'ink'}
        className={cx('truncate', wide ? 'text-sm' : 'text-base', done && 'line-through')}
      >
        {name}
      </Text>
      {subtext && !collapsed && !wide && (
        <Text variant="caption" tone="subtle" className="truncate">
          {subtext}
        </Text>
      )}
      {/* `card` only, and never once folded: `row` keeps a uniform height
          because that is what holds the day columns in line. */}
      {below && !collapsed && !wide && <Box className="pt-1.5">{below}</Box>}
    </VStack>
  );

  return (
    <HStack
      as="li"
      gap={wide ? 0 : 3}
      align="center"
      style={style}
      className={cx(
        'transition-colors duration-[var(--aj-duration-base)] ease-standard',
        'motion-reduce:transition-none',
        wide
          ? 'group h-11 w-max border-b border-line hover:bg-sunken'
          : cx(
              'rounded-xl border',
              collapsed
                ? 'min-h-12 border-line/60 bg-transparent py-2 pr-3.5 pl-4'
                : 'min-h-16 border-line bg-surface py-3 pr-3.5 pl-4',
            ),
        className,
      )}
    >
      {wide ? (
        // Grip and name travel together and stay put: one sticky block, so a
        // month of scrolled squares never leaves anonymous rows on screen.
        <HStack
          gap={2}
          align="center"
          // Opaque, and the same surface as the card it sits on: the days
          // scroll underneath it. `group-hover` because an opaque block can't
          // inherit the row's hover tint.
          className={cx(
            LEAD_WIDTH,
            'sticky left-0 z-10 shrink-0 bg-surface pr-3 pl-2 group-hover:bg-sunken',
          )}
        >
          {leading}
          {label}
        </HStack>
      ) : (
        <>
          {leading}
          {label}
        </>
      )}

      {trailing}
    </HStack>
  );
}
